import { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, Bot, User, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { tenantService } from '@/services/tenantService';
import { ravanGlobalCore, type RavanMode } from '@/services/ravan/ravanGlobalCore';
import type { ChatMessage } from '@/services/ravan/ravanCore';
import type { ParsedCommand } from '@/services/ravan/commandInterpreter';
import { useRavanStore } from '@/stores/ravanStore';

const SUGGESTED_PROMPTS = [
  "Create a new quote for a client",
  "Add an item to my catalogue",
  "Analyze pricing trends for wardrobe items",
  "Generate a client summary for my latest quote"
];

export function GlobalRavanOverlay() {
  const { isOpen, closeRavan } = useRavanStore();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [mode, setMode] = useState<RavanMode>('global');
  const [pinnedQuoteId, setPinnedQuoteId] = useState<string>();
  const [isPro, setIsPro] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkPlan = async () => {
      try {
        const tenant = await tenantService.getCurrentTenant();
        setIsPro(tenant?.plan === 'pro' || tenant?.plan === 'enterprise');
      } catch (err) {
        setIsPro(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkPlan();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isProcessing, isOpen]);

  const handleSend = async (text: string) => {
    if (!text.trim() || isProcessing) return;
    
    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsProcessing(true);

    try {
      const response = await ravanGlobalCore.processMessage(
        text,
        messages.concat(userMsg),
        mode,
        pinnedQuoteId
      );

      const ravanMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'ravan',
        content: response.reply,
        timestamp: new Date().toISOString(),
        command: response.command
      };

      setMessages(prev => [...prev, ravanMsg]);
    } catch (err) {
      toast.error('Failed to process message');
    } finally {
      setIsProcessing(false);
    }
  };

  const executeCommand = async (msgId: string, command: ParsedCommand) => {
    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, isLoading: true } : m));
    
    const result = await ravanGlobalCore.executeGlobalAction(command, pinnedQuoteId);
    
    setMessages(prev => prev.map(m => m.id === msgId ? {
      ...m,
      isLoading: false,
      toolResult: result
    } : m));

    if (result.ok) {
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
  };

  if (isLoading || !isPro) return null; // Only render for Pro users

  return (
    <>
      {/* Backdrop overlay (optional: uncomment if you want clicking outside to close it)
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-[1px] z-[99] transition-opacity duration-300" 
          onClick={closeRavan}
        />
      )}
      */}

      {/* Slide-over Panel */}
      <div 
        className={cn(
          "fixed top-0 right-0 h-full w-[400px] max-w-full bg-zinc-50 dark:bg-[#0A0A0A] shadow-[0_0_40px_rgba(0,0,0,0.1)] dark:shadow-[0_0_40px_rgba(0,0,0,0.5)] z-[100] border-l border-border flex flex-col transition-transform duration-300 ease-out",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* Header */}
        <header className="flex-none h-16 border-b border-border bg-white/50 dark:bg-zinc-950/50 backdrop-blur-md flex items-center px-4 justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <h1 className="text-sm font-bold tracking-tight">RAVAN</h1>
              <p className="text-[10px] uppercase tracking-widest text-emerald-500 font-semibold">Global AI Copilot</p>
            </div>
          </div>
          
          <div className="flex gap-2 items-center">
            <select 
              value={mode}
              onChange={(e) => setMode(e.target.value as RavanMode)}
              className="text-[11px] bg-muted/50 border-none rounded-lg px-2 py-1.5 focus:ring-1 focus:ring-emerald-500 cursor-pointer text-muted-foreground outline-none font-medium"
            >
              <option value="global">Global Mode</option>
              <option value="catalogue">Catalogue</option>
              <option value="analytics">Analytics</option>
            </select>
            <button 
              onClick={closeRavan}
              className="h-8 w-8 rounded-lg bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground flex items-center justify-center transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        {/* Chat Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-5">
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-tr from-emerald-500/20 to-teal-500/20 flex items-center justify-center">
                <Bot className="h-7 w-7 text-emerald-500" />
              </div>
              <div>
                <h2 className="text-lg font-bold mb-1.5">How can I help?</h2>
                <p className="text-xs text-muted-foreground max-w-[250px] mx-auto">
                  I can create quotes, manage your catalogue, or analyze pricing trends.
                </p>
              </div>
              
              <div className="flex flex-col gap-2 w-full max-w-[280px]">
                {SUGGESTED_PROMPTS.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(prompt)}
                    className="text-left px-3 py-2 rounded-lg border border-border bg-white/50 dark:bg-zinc-900/50 hover:bg-surface-hover text-xs text-muted-foreground hover:text-foreground transition-all"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map(msg => (
            <div key={msg.id} className={cn("flex gap-3", msg.role === 'user' ? "flex-row-reverse" : "")}>
              <div className={cn(
                "h-7 w-7 shrink-0 rounded-full flex items-center justify-center shadow-sm",
                msg.role === 'user' ? "bg-primary text-primary-foreground" : "bg-emerald-500/10 text-emerald-500"
              )}>
                {msg.role === 'user' ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
              </div>
              
              <div className={cn("flex flex-col gap-1.5 max-w-[85%]", msg.role === 'user' ? "items-end" : "items-start")}>
                <div className={cn(
                  "px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed",
                  msg.role === 'user' 
                    ? "bg-primary text-primary-foreground rounded-tr-sm" 
                    : "bg-white dark:bg-zinc-900 border border-border shadow-sm rounded-tl-sm"
                )}>
                  {msg.content}
                </div>

                {msg.command && !msg.toolResult && (
                  <div className="bg-white dark:bg-zinc-900 border border-border rounded-xl p-3.5 mt-1 w-full shadow-sm">
                    <p className="text-[10px] font-semibold text-emerald-500 mb-1 uppercase tracking-wider">Proposed Action</p>
                    <p className="text-sm font-medium mb-3 leading-snug">{msg.command.displayLabel}</p>
                    <Button 
                      size="sm" 
                      onClick={() => executeCommand(msg.id, msg.command!)}
                      disabled={msg.isLoading}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm"
                    >
                      {msg.isLoading ? 'Working...' : 'Confirm'}
                    </Button>
                  </div>
                )}

                {msg.toolResult && (
                  <div className={cn(
                    "text-xs px-2.5 py-1.5 rounded-lg border w-full text-center font-medium",
                    msg.toolResult.ok ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-400" : "bg-rose-500/10 border-rose-500/20 text-rose-700 dark:text-rose-400"
                  )}>
                    {msg.toolResult.ok ? '✓ Action successful' : '⚠ Action failed'}
                  </div>
                )}
              </div>
            </div>
          ))}
          {isProcessing && (
            <div className="flex gap-3">
              <div className="h-7 w-7 shrink-0 rounded-full bg-emerald-500/10 text-emerald-500 flex items-center justify-center shadow-sm">
                <Bot className="h-3.5 w-3.5" />
              </div>
              <div className="bg-white dark:bg-zinc-900 border border-border shadow-sm rounded-2xl rounded-tl-sm px-3.5 py-2.5 flex items-center gap-1">
                <div className="w-1.5 h-1.5 bg-emerald-500/50 rounded-full animate-bounce" />
                <div className="w-1.5 h-1.5 bg-emerald-500/50 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                <div className="w-1.5 h-1.5 bg-emerald-500/50 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 bg-white/50 dark:bg-zinc-950/50 backdrop-blur-md border-t border-border shrink-0">
          <div className="relative">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(input);
                }
              }}
              placeholder="Ask Ravan..."
              className="w-full resize-none rounded-xl border border-border bg-white dark:bg-zinc-900 px-3 py-3 pr-11 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 min-h-[48px] max-h-32 shadow-inner"
              rows={1}
            />
            <button
              onClick={() => handleSend(input)}
              disabled={!input.trim() || isProcessing}
              className="absolute right-1.5 bottom-1.5 h-9 w-9 rounded-lg bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600 disabled:opacity-50 transition-colors shadow-sm"
            >
              <Send className="h-4 w-4 shrink-0 -ml-0.5" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
